import * as React from 'react'
import { observer, inject } from 'mobx-react'
import { StateType } from '../state/state'
import { formatSIWithIntegerThreshold } from '../../../lib/format-utils'
import { useEffect, useState, useRef } from 'react'
import { clamp } from 'lodash-es'
import { DraggableRect } from './DraggableRectangle'
import { DraggableCircle } from './DraggableCircle'
import {
  SPACE_BETWEEN_AXIS_AND_SCATTER_AREA,
  SPACE_BETWEEN_AXIS_AND_AXIS_LABEL,
  DRAGGABLE_BAR_WIDTH,
  UNDERGROUND_BAR_WIDTH,
} from '../constants'

interface Props {
  className?: string
  state?: StateType
  xScale: any
  yScale: any
  yScaleScrollbar: any
  xScaleScrollbar: any
  axisName: string
  type?: string
  onDragChange: any
  handleRadius?: number
  draggingHandleRadius?: number
}

export const FilterableAxis: React.FC<Props> = inject('state')(
  observer(
    ({
      className = '',
      state,
      xScale,
      yScale,
      yScaleScrollbar,
      xScaleScrollbar,
      axisName,
      type = 'x',
      onDragChange,
      handleRadius = 5,
      draggingHandleRadius = 10,
    }) => {
      const {
        xDomain,
        yDomain,
        setXDomainMinOverride,
        setXDomainMaxOverride,
        setYDomainMinOverride,
        setYDomainMaxOverride,
        debug,
      } = state.ScatterplotState

      const isX = type === 'x'

      const [lowerX, upperX] = xDomain.map(xScaleScrollbar)
      const [upperY, lowerY] = yDomain.map(yScaleScrollbar)

      const axisNameRef = useRef(null)
      const [axisNameSize, setAxisNameSize] = useState({ w: 0, h: 0 })
      const [lastDragged, setLastDragged] = useState<'lower' | 'upper'>('lower')

      useEffect(() => {
        if (axisNameRef.current) {
          const { width: w, height: h } = axisNameRef.current.getBBox()
          const size = isX ? { w: w + 5, h: h + 10 } : { w: h + 10, h: w + 20 }
          setAxisNameSize(size)
        }
      }, [axisName])

      const handleAreaDrag = ({ deltaX, deltaY }: { deltaX: number; deltaY: number }) => {
        if (isX) {
          const [lowerBound, upperBound] = xScaleScrollbar.range()
          const upperDeltaX = upperBound - upperX
          const lowerDeltaX = lowerBound - lowerX
          const moveX = clamp(deltaX, lowerDeltaX, upperDeltaX)
          setXDomainMinOverride(xScaleScrollbar.invert(lowerX + moveX))
          setXDomainMaxOverride(xScaleScrollbar.invert(upperX + moveX))
        } else {
          const [upperBound, lowerBound] = yScaleScrollbar.range()
          const upperDeltaY = upperBound - upperY
          const lowerDeltaY = lowerBound - lowerY
          const moveY = clamp(deltaY, lowerDeltaY, upperDeltaY)
          setYDomainMinOverride(yScaleScrollbar.invert(upperY + moveY))
          setYDomainMaxOverride(yScaleScrollbar.invert(lowerY + moveY))
        }
      }

      const handleLowerDrag = ({ deltaX, deltaY }: { deltaX: number; deltaY: number }) => {
        setLastDragged('lower')
        if (isX) {
          const newLowerDomain = xScaleScrollbar.invert(lowerX + deltaX)
          const minDomain = xScaleScrollbar.domain()[0]
          const maxDomain = xDomain[1]
          const clampedNewLowerDomain = clamp(newLowerDomain, minDomain, maxDomain)
          setXDomainMinOverride(clampedNewLowerDomain)
        } else {
          const newLowerDomain = yScaleScrollbar.invert(upperY + deltaY)
          const minDomain = yScaleScrollbar.domain()[0]
          const maxDomain = yDomain[1]
          const clampedNewLowerDomain = clamp(newLowerDomain, minDomain, maxDomain)
          setYDomainMinOverride(clampedNewLowerDomain)
        }
      }

      const handleUpperDrag = ({ deltaX, deltaY }: { deltaX: number; deltaY: number }) => {
        setLastDragged('upper')
        if (isX) {
          const newUpperDomain = xScaleScrollbar.invert(upperX + deltaX)
          const minDomain = xDomain[0]
          const maxDomain = xScaleScrollbar.domain()[1]
          const clampedNewUpperDomain = clamp(newUpperDomain, minDomain, maxDomain)
          setXDomainMaxOverride(clampedNewUpperDomain)
        } else {
          const newUpperDomain = yScaleScrollbar.invert(lowerY + deltaY)
          const minDomain = yDomain[0]
          const maxDomain = yScaleScrollbar.domain()[1]
          const clampedNewUpperDomain = clamp(newUpperDomain, minDomain, maxDomain)
          setYDomainMaxOverride(clampedNewUpperDomain)
        }
      }

      const [isDragging, setIsDragging] = useState(false)
      const handleDragStart = () => setIsDragging(true)
      const handleDragEnd = () => setIsDragging(false)

      useEffect(() => onDragChange && onDragChange(isDragging), [onDragChange, isDragging])

      const x1 = xScale.range()[0]
      const x2 = isX ? xScale.range()[1] : xScale.range()[0]
      const y1 = isX ? yScale.range()[0] : yScale.range()[0]
      const y2 = isX ? yScale.range()[0] : yScale.range()[1]

      const fillOpacity = 1

      const lowerCircle = (
        <DraggableCircle
          key="lower"
          onDrag={handleLowerDrag}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          cx={
            isX
              ? xScaleScrollbar(xDomain[0]) - DRAGGABLE_BAR_WIDTH / 2
              : x1 - DRAGGABLE_BAR_WIDTH / 2
          }
          cy={
            isX
              ? y1 - DRAGGABLE_BAR_WIDTH / 2
              : yScaleScrollbar(yDomain[0]) - DRAGGABLE_BAR_WIDTH / 2
          }
          fillOpacity={fillOpacity}
          radius={handleRadius}
          draggingRadius={draggingHandleRadius}
        />
      )
      const upperCircle = (
        <DraggableCircle
          key="upper"
          onDrag={handleUpperDrag}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          cx={
            isX
              ? xScaleScrollbar(xDomain[1]) - DRAGGABLE_BAR_WIDTH / 2
              : x1 - DRAGGABLE_BAR_WIDTH / 2
          }
          cy={
            isX
              ? y1 - DRAGGABLE_BAR_WIDTH / 2
              : yScaleScrollbar(yDomain[1]) - DRAGGABLE_BAR_WIDTH / 2
          }
          fillOpacity={fillOpacity}
          radius={handleRadius}
          draggingRadius={draggingHandleRadius}
        />
      )
      const circles =
        lastDragged === 'lower' ? [upperCircle, lowerCircle] : [lowerCircle, upperCircle]

      return (
        <g
          className={`-${type}-filterable-axis ${className}`}
          transform={
            isX
              ? `translate(${0}, ${SPACE_BETWEEN_AXIS_AND_SCATTER_AREA})`
              : `translate(${-SPACE_BETWEEN_AXIS_AND_SCATTER_AREA}, ${0})`
          }
        >
          {debug && (
            <line
              className={`-${type}-axis-line`}
              x1={x1}
              x2={x2}
              y1={y1}
              y2={y2}
              stroke="red"
              strokeWidth={1}
            />
          )}
          <rect
            className={`-${type}-axis-background-rect`}
            x={
              isX
                ? x1 - DRAGGABLE_BAR_WIDTH / 2
                : x1 - UNDERGROUND_BAR_WIDTH / 2 - DRAGGABLE_BAR_WIDTH / 2
            }
            y={
              isX
                ? y1 - UNDERGROUND_BAR_WIDTH / 2 - DRAGGABLE_BAR_WIDTH / 2
                : y2 - DRAGGABLE_BAR_WIDTH / 2
            }
            width={isX ? x2 - x1 : UNDERGROUND_BAR_WIDTH}
            height={isX ? UNDERGROUND_BAR_WIDTH : y1 - y2}
            fill="black"
            fillOpacity={0.1}
          />

          {/* axis name */}
          <rect
            x={
              isX
                ? x1 + (x2 - x1) / 2 - axisNameSize.w / 2
                : x1 - SPACE_BETWEEN_AXIS_AND_AXIS_LABEL - axisNameSize.w
            }
            y={
              isX
                ? y1 + SPACE_BETWEEN_AXIS_AND_AXIS_LABEL - axisNameSize.h / 2
                : y1 + (y2 - y1) / 2 - axisNameSize.h / 2
            }
            width={axisNameSize.w}
            height={axisNameSize.h}
            fill="white"
            fillOpacity={0.9}
          />
          <text
            ref={axisNameRef}
            className="f6 fw6 user-select-none"
            x={isX ? x1 + (x2 - x1) / 2 : x1 - SPACE_BETWEEN_AXIS_AND_AXIS_LABEL}
            y={isX ? y1 + SPACE_BETWEEN_AXIS_AND_AXIS_LABEL : y1 + (y2 - y1) / 2}
            fill="var(--gray900)"
            textAnchor={isX ? 'middle' : 'middle'}
            dominantBaseline={isX ? 'hanging' : 'baseline'}
            transform={
              isX
                ? ``
                : `rotate(${-90}, ${x1 - SPACE_BETWEEN_AXIS_AND_AXIS_LABEL}, ${y1 + (y2 - y1) / 2})`
            }
          >
            {axisName}
          </text>

          {/* min value label */}
          <text
            className="-min-value-label user-select-none f5"
            x={isX ? xScale(xDomain[0]) : x1 - SPACE_BETWEEN_AXIS_AND_AXIS_LABEL}
            y={isX ? y1 + SPACE_BETWEEN_AXIS_AND_AXIS_LABEL : yScale(yDomain[0])}
            fill="var(--gray500)"
            textAnchor={isX ? 'middle' : 'end'}
            dominantBaseline={isX ? 'hanging' : 'baseline'}
          >
            {isX
              ? formatSIWithIntegerThreshold(xDomain[0])
              : formatSIWithIntegerThreshold(yDomain[0])}
          </text>

          {/* max value label */}
          <text
            className="-max-value-label user-select-none f5"
            x={isX ? xScale(xDomain[1]) : x1 - SPACE_BETWEEN_AXIS_AND_AXIS_LABEL}
            y={
              isX ? y1 + SPACE_BETWEEN_AXIS_AND_AXIS_LABEL : yScale(yDomain[1]) - 3 // -3 it is only for an optical problem
            }
            fill="var(--gray500)"
            textAnchor={isX ? 'middle' : 'end'}
            dominantBaseline="hanging"
          >
            {isX
              ? formatSIWithIntegerThreshold(xDomain[1])
              : formatSIWithIntegerThreshold(yDomain[1])}
          </text>
          <g>
            <DraggableRect
              onDrag={handleAreaDrag}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              x={isX ? xScaleScrollbar(xDomain[0]) : x1 - DRAGGABLE_BAR_WIDTH}
              y={isX ? y1 - DRAGGABLE_BAR_WIDTH : yScaleScrollbar(yDomain[1])}
              width={
                isX
                  ? Math.abs(xScaleScrollbar(xDomain[1]) - xScaleScrollbar(xDomain[0]))
                  : DRAGGABLE_BAR_WIDTH
              }
              height={
                isX
                  ? DRAGGABLE_BAR_WIDTH
                  : Math.abs(yScaleScrollbar(yDomain[1]) - yScaleScrollbar(yDomain[0]))
              }
              fillOpacity={fillOpacity}
            />
            {circles}
          </g>
        </g>
      )
    }
  )
)
